import { UseFormReturn } from "react-hook-form";
import { Pagination } from "src/components/ui/pagination";
import useSearch from "src/hooks/useSearch";
import { useGetProductVariantList } from "src/pages/ProductManagement/ProductList/api/queries";
import { AddProductModalSchemaType } from "../addProductModalSchema";
import { SelectedPackageProductType } from "./usePOSHandler";
import { useEffect, useState } from "react";

interface UseAddProductModalPackageProductListHandlerProps {
  form: UseFormReturn<AddProductModalSchemaType, any, undefined>;
  index: number;
  packageVariantId: number;
  selectedPackageProducts: SelectedPackageProductType[];
  setSelectedPackageProducts: React.Dispatch<
    React.SetStateAction<SelectedPackageProductType[]>
  >;
  packageProduct: AddProductModalSchemaType["products"][number];
}

const useAddProductModalPackageProductListHandler = ({
  form,
  index,
  packageVariantId,
  selectedPackageProducts,
  setSelectedPackageProducts,
  packageProduct,
}: UseAddProductModalPackageProductListHandlerProps) => {
  const [isInit, setIsInit] = useState(true);

  const {
    value: searchProductVariantList,
    setSearch: setSearchProductVariantList,
  } = useSearch();

  const {
    data: productVariantListData,
    isLoading: isLoadingProductVariantListData,
  } = useGetProductVariantList({
    ...Pagination,
    ...(searchProductVariantList ? { name: searchProductVariantList } : {}),
    selectedVariantIds: [],
  });

  const productVariants = productVariantListData?.data ?? [];
  const productVariantListOptions =
    productVariants.map((pvld) => ({
      label: `${pvld.product.name} - ${pvld.name}`,
      value: `${pvld.id}`,
    })) ?? [];

  const formValues = form.getValues();

  const selectedProductVariantId = formValues.products[index].id;
  const selectedPackageProduct = selectedPackageProducts.find(
    (selectedPackageProduct) =>
      `${selectedPackageProduct.id}` === `${selectedProductVariantId}` &&
      `${selectedPackageProduct.packageVariantId}` === `${packageVariantId}`
  );

  let filteredProductVariantListOptions = [...productVariantListOptions];

  if (selectedPackageProduct) {
    filteredProductVariantListOptions = [
      {
        label: `${selectedPackageProduct.productName} - ${selectedPackageProduct.variantName}`,
        value: `${selectedPackageProduct.id}`,
      },
      ...productVariantListOptions,
    ];
  }

  const selectedProduct = selectedPackageProduct
    ? {
        stocks: selectedPackageProduct.stocks?.map((stock) => ({
          ...stock,
          warehouseId: stock.warehouse_id,
        })),
        is_rent: selectedPackageProduct.isRent,
        is_purchase: selectedPackageProduct.isPurchase,
        rent_price: selectedPackageProduct.rentPrice,
        sell_price: selectedPackageProduct.sellPrice,
        id: selectedPackageProduct.id,
        name: selectedPackageProduct.variantName,
        product: {
          name: selectedPackageProduct.productName,
          item_stock_unit: {
            name: selectedPackageProduct.itemStockUnit,
          },
        },
      }
    : productVariants.find(
        (productVariant) => `${productVariant.id}` === `${packageProduct.id}`
      );
  const stocks =
    selectedPackageProduct?.stocks ?? selectedProduct?.stocks ?? [];
  const warehouseOptions =
    stocks.map((stock) => ({
      label: stock.warehouse.name,
      value: `${stock.warehouse?.id}`,
    })) ?? [];

  const selectedStock = (stocks as any)?.find(
    (stock: any) => `${stock.warehouse.id}` === `${packageProduct.warehouseId}`
  );
  const hasStock = (selectedStock?.available ?? 0) > 0;
  const availableStock = selectedStock?.available ?? 0;

  const onChangeProductItemCallback = (value: any) => {
    const selectedPkgProduct = selectedPackageProducts.find(
      (selectedPackageProduct) =>
        `${selectedPackageProduct.id}` === value &&
        `${selectedPackageProduct.packageVariantId}` === `${packageVariantId}`
    );
    const selectedProductVariant = productVariants?.find(
      (productVariant) => `${productVariant.id}` === `${value}`
    );

    form.setValue(`products.${index}.warehouseId`, "0", {
      shouldValidate: true,
    });
    form.setValue(`products.${index}.warehouseName`, "", {
      shouldValidate: true,
    });
    form.setValue(
      `products.${index}.quantity`,
      `${selectedPkgProduct?.quantity ?? 0}`,
      {
        shouldValidate: true,
      }
    );

    if (selectedProductVariant) {
      setSelectedPackageProducts((prevState) => {
        let newArr = [...prevState];

        const isExist = prevState.find(
          (spp) => `${spp.id}` === `${selectedProductVariant.id}`
        );

        if (!isExist) {
          newArr.push({
            id: selectedProductVariant.id,
            packageVariantId,
            productName: selectedProductVariant?.product.name,
            variantName: selectedProductVariant.name,
            itemStockUnit:
              selectedProductVariant.product?.item_stock_unit?.name,
            stocks:
              selectedProductVariant.stocks.map((stock) => ({
                ...stock,
                product_variant_id: selectedProductVariant.id,
                warehouse_id: `${stock.warehouseId}` as any,
              })) ?? [],
            quantity: 0,
            isRent: selectedProductVariant.is_rent,
            isPurchase: selectedProductVariant.is_purchase,
            rentPrice: selectedProductVariant.rent_price,
            sellPrice: selectedProductVariant.sell_price,
          });
        }

        return newArr;
      });
    }
  };
  const onChangeWarehouseCallback = (value: any) => {
    const selectedStock = (stocks as any)?.find(
      (stock: any) => `${stock.warehouse.id}` === value
    );

    const productQuantity = selectedPackageProduct?.quantity ?? 0;
    const stockAvailability = selectedStock?.available;
    const isAvailable = stockAvailability >= productQuantity;

    if (isAvailable) {
      form.setValue(`products.${index}.quantity`, `${productQuantity}`, {
        shouldValidate: true,
      });
    } else {
      form.setValue(`products.${index}.quantity`, `${availableStock}`, {
        shouldValidate: true,
      });
    }

    form.setValue(
      `products.${index}.warehouseName`,
      selectedStock?.warehouse?.name,
      {
        shouldValidate: true,
      }
    );
  };

  const handleDeleteProductList = (index: number) => {
    let newProducts = [...form.getValues().products];

    delete newProducts[index];

    newProducts = newProducts.filter(Boolean);

    form.setValue("products", newProducts, {
      shouldValidate: true,
    });
  };

  const isPurchaseOnly =
    selectedProduct?.is_purchase && !selectedProduct?.is_rent;
  const isRentOnly = !selectedProduct?.is_purchase && selectedProduct?.is_rent;
  const isAllowForRent = selectedProduct?.is_rent;

  useEffect(() => {
    if (isRentOnly) {
      form.setValue(`products.${index}.markAsRent`, true, {
        shouldValidate: true,
      });
      form.setValue(
        `products.${index}.price`,
        `${selectedProduct.rent_price}`,
        {
          shouldValidate: true,
        }
      );
    } else if (isPurchaseOnly) {
      form.setValue(`products.${index}.markAsRent`, false, {
        shouldValidate: true,
      });
      form.setValue(
        `products.${index}.price`,
        `${selectedProduct.sell_price}`,
        {
          shouldValidate: true,
        }
      );
    }
  }, [isRentOnly, isPurchaseOnly, index, form]);

  const totalItemPrice = Math.max(
    Math.max(
      parseInt(`${packageProduct.price}`, 0) *
        parseInt(`${packageProduct.quantity}`, 0),
      0
    ) - parseInt(`${packageProduct.discount}`, 0),
    0
  );

  const uniqueObjects = (arr: { label: string; value: string }[]) => {
    const seen = new Set();
    return arr.filter((item) => !seen.has(item.value) && seen.add(item.value));
  };

  const handleMarkAsRentCallBack = ({
    checked,
    index,
  }: {
    checked: boolean;
    index: number;
  }) => {
    if (checked) {
      const rentPrice = selectedProduct?.rent_price ?? 0;

      form.setValue(`products.${index}.price`, `${rentPrice}`, {
        shouldValidate: true,
      });
    } else {
      const sellPrice = selectedProduct?.sell_price ?? 0;

      form.setValue(`products.${index}.price`, `${sellPrice}`, {
        shouldValidate: true,
      });
    }
  };

  return {
    productVariantListOptions: uniqueObjects(filteredProductVariantListOptions),
    isLoadingProductVariantListData,
    setSearchProductVariantList,
    warehouseOptions,
    selectedPackageProduct,
    availableStock,
    selectedStock,
    hasStock,
    totalItemPrice,
    isRentOnly,
    isAllowForRent,
    onChangeWarehouseCallback,
    onChangeProductItemCallback,
    handleDeleteProductList,
    handleMarkAsRentCallBack,
  };
};

export default useAddProductModalPackageProductListHandler;
